<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import ParticipantManagementModal from './ParticipantManagementModal.vue';
import TournamentResultsModal from '@tournaments/TournamentResultsModal.vue';
import RosterSelectionModal from './RosterSelectionModal.vue';
import Avatar from '../shared/Avatar.vue';

interface Props {
  event?: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  join: [event: any];
  'event-updated': [event: any];
}>();

const { t, locale } = useI18n();

// Defensive normalization: sometimes parent can pass `undefined` or an object without `attributes`
// (e.g. unexpected API payload / transient state). Ensure template & computed props never crash.
const event = computed(() => {
  const e = props.event;
  if (e && typeof e === 'object') {
    if (!e.attributes || typeof e.attributes !== 'object') {
      // Vue props are shallow readonly; nested mutation is allowed and keeps reactivity for callers
      e.attributes = {};
    }
    return e;
  }
  return { attributes: {} };
});

const isTournament = computed(() => {
  return event.value.attributes.event_type === 3 || event.value.attributes.event_type === 'tournament';
});

// Get localized event type name
const eventTypeName = computed(() => {
  const eventTypeKey = event.value.attributes.event_type_key;
  if (!eventTypeKey) {
    // Fallback to original name if event_type_key not available
    return event.value.attributes.event_type_name || '';
  }

  return t(`activerecord.attributes.event.event_types.${eventTypeKey}`);
});

const requiredRostersCount = computed(() => {
  if (
    isTournament.value &&
    event.value.attributes.data &&
    typeof event.value.attributes.data === 'object' &&
    'required_rosters_count' in event.value.attributes.data
  ) {
    return Number(event.value.attributes.data.required_rosters_count) || 1;
  }
  return 1;
});

// Description expand/collapse
const isDescriptionExpanded = ref(false);
const descriptionMaxLength = 200;

const truncatedDescription = computed(() => {
  const description = event.value.attributes.description || '';
  if (!description) return '';

  // Strip HTML tags for length calculation
  const textContent = description.replace(/<[^>]*>/g, '');
  if (textContent.length <= descriptionMaxLength) {
    return description;
  }

  // Get the first part up to max length (in text content)
  let truncatedText = textContent.substring(0, descriptionMaxLength);

  // Find the last space, newline, or tab before the cut point
  // This prevents cutting words in the middle
  const lastSpaceIndex = Math.max(
    truncatedText.lastIndexOf(' '),
    truncatedText.lastIndexOf('\n'),
    truncatedText.lastIndexOf('\t')
  );

  // If we found a space/newline/tab, cut there instead
  // But only if it's not too close to the beginning (at least 80% of max length)
  const minLength = Math.floor(descriptionMaxLength * 0.8);
  if (lastSpaceIndex > minLength) {
    truncatedText = truncatedText.substring(0, lastSpaceIndex);
  }

  // Now we need to find the corresponding position in the HTML
  // This is a simplified approach - we'll try to preserve HTML structure
  // For a more robust solution, we'd need to parse the HTML properly
  const truncatedLength = truncatedText.length;
  let textLength = 0;
  let inTag = false;
  let result = '';

  for (let i = 0; i < description.length && textLength < truncatedLength; i++) {
    const char = description[i];
    if (char === '<') {
      inTag = true;
      result += char;
    } else if (char === '>') {
      inTag = false;
      result += char;
    } else if (inTag) {
      result += char;
    } else {
      result += char;
      textLength++;
    }
  }

  // Try to close any open tags
  return result;
});

// Removed remainingDescription computed as we now show full HTML when expanded

const showReadMore = computed(() => {
  const description = event.value.attributes.description || '';
  // Strip HTML tags for length calculation
  const textContent = description.replace(/<[^>]*>/g, '');
  // Show "Подробнее" button if description is long OR if it's a tournament with rounds/missions
  return textContent.length > descriptionMaxLength || (isTournament.value && tournamentRoundsCount.value > 0);
});

const toggleDescription = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
};

// Transition hooks for slide animation
const onBeforeEnter = (el: Element) => {
  const element = el as HTMLElement;
  // Set initial state for slide down
  element.style.height = '0';
  element.style.opacity = '1';
  element.style.marginTop = '0';
  element.style.overflow = 'hidden';
  element.style.paddingTop = '0';
  element.style.paddingBottom = '0';
};

const onEnter = (el: Element, done: () => void) => {
  const element = el as HTMLElement;
  // Get the actual height of the content
  const height = element.scrollHeight;

  // Force reflow to ensure initial state is applied
  void element.offsetHeight;

  requestAnimationFrame(() => {
    // Animate to full height (slide down)
    element.style.transition = 'height 0.5s ease-out, padding-top 0.5s ease-out, padding-bottom 0.5s ease-out';
    element.style.height = `${height}px`;
    element.style.paddingTop = '';
    element.style.paddingBottom = '';

    // After animation completes, set to auto for responsive behavior
    setTimeout(() => {
      element.style.height = 'auto';
      done();
    }, 500);
  });
};

const onBeforeLeave = (el: Element) => {
  const element = el as HTMLElement;
  // Get the actual height before starting leave animation
  // If height is 'auto', we need to get scrollHeight
  const currentHeight = element.style.height;
  let height: number;

  if (currentHeight === 'auto' || !currentHeight || currentHeight === '') {
    // Get accurate scrollHeight
    element.style.height = 'auto';
    height = element.scrollHeight;
    element.style.height = `${height}px`;
  } else {
    // Already has a pixel value, use it
    height = parseInt(currentHeight) || element.scrollHeight;
    element.style.height = `${height}px`;
  }

  element.style.overflow = 'hidden';
  // Force reflow to ensure height is set
  void element.offsetHeight;
};

const onLeave = (el: Element, done: () => void) => {
  const element = el as HTMLElement;
  // Get the height that was set in onBeforeLeave
  const height = parseInt(element.style.height) || element.scrollHeight;

  // Ensure we have a valid height
  if (height === 0 || isNaN(height)) {
    done();
    return;
  }

  // Force reflow to ensure the height is applied
  void element.offsetHeight;

  requestAnimationFrame(() => {
    // Animate to zero height (slide up)
    element.style.transition = 'height 0.5s ease-in, padding-top 0.5s ease-in, padding-bottom 0.5s ease-in';
    element.style.height = '0';
    element.style.paddingTop = '0';
    element.style.paddingBottom = '0';

    setTimeout(() => {
      done();
    }, 500);
  });
};

// Tournament rounds and missions
const tournamentRounds = computed(() => {
  if (!isTournament.value || !event.value.attributes.data) return [];

  const data = event.value.attributes.data;
  // Check both 'rounds' (frontend format) and 'tournament_rounds' (backend format)
  const rounds = data.rounds || data.tournament_rounds || [];
  return Array.isArray(rounds) ? rounds : [];
});

const tournamentRoundsCount = computed(() => {
  return tournamentRounds.value.length;
});

// Load scenarios list for mapping scenario_id to names
const scenariosList = ref<Array<{ id: number; name: string }>>([]);
// Load maps list for mapping map_id to names
const mapsList = ref<Array<{ id: number; name: string }>>([]);

const loadScenarios = async () => {
  try {
    const currentLocale = locale.value === 'en' ? 'en' : 'ru';
    const response = await axios.get('/api/dict/v1/scenarios', {
      headers: {
        'Accept-Language': currentLocale
      }
    });
    scenariosList.value = response.data;
  } catch (error) {
    console.error('Error loading scenarios:', error);
  }
};

const loadMaps = async () => {
  try {
    const currentLocale = locale.value === 'en' ? 'en' : 'ru';
    const response = await axios.get('/api/dict/v1/maps', {
      headers: {
        'Accept-Language': currentLocale
      }
    });
    mapsList.value = response.data;
  } catch (error) {
    console.error('Error loading maps:', error);
  }
};

const getScenarioName = (scenarioId: number | string): string => {
  const id = typeof scenarioId === 'string' ? parseInt(scenarioId) : scenarioId;
  const scenario = scenariosList.value.find(s => s.id === id);
  return scenario ? scenario.name : `${t('events.mission')} ${id}`;
};

const getMapName = (mapId: number | string): string => {
  const id = typeof mapId === 'string' ? parseInt(mapId) : mapId;
  const map = mapsList.value.find(m => m.id === id);
  return map ? map.name : '';
};

const tournamentMissions = computed(() => {
  const rounds = tournamentRounds.value;
  if (!rounds || rounds.length === 0) return [];

  // Return all rounds with their data
  return rounds.map((round: any, index: number) => {
    const roundNumber = round.round_number || round.id || index + 1;

    let scenarioName = '';
    if (round.scenario) {
      scenarioName = round.scenario.name || getScenarioName(round.scenario.id || round.scenario_id);
    } else if (round.scenario_id) {
      scenarioName = getScenarioName(round.scenario_id);
    }

    let mapName = '';
    if (round.map) {
      mapName = round.map.name || getMapName(round.map.id || round.map_id);
    } else if (round.map_id) {
      mapName = getMapName(round.map_id);
    }

    return {
      id: round.id || roundNumber,
      roundNumber: roundNumber,
      scenarioName: scenarioName,
      mapName: mapName
    };
  });
});

// Load scenarios and maps on component mount
onMounted(() => {
  if (isTournament.value) {
    loadScenarios();
    loadMaps();
  }
});

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  // Check if date is valid (not NaN or Invalid Date)
  if (isNaN(date.getTime())) return '';

  // Get locale from i18n
  const dateLocale = locale.value === 'en' ? 'en-US' : 'ru-RU';

  // Use Intl.DateTimeFormat with proper locale
  const formatter = new Intl.DateTimeFormat(dateLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return formatter.format(date);
};

const showParticipantModal = ref(false);
const showTournamentResultsModal = ref(false);
const showRosterModal = ref(false);

const openParticipantModal = () => {
  showParticipantModal.value = true;
};

const closeParticipantModal = () => {
  showParticipantModal.value = false;
};

const openTournamentResultsModal = () => {
  showTournamentResultsModal.value = true;
};

const closeTournamentResultsModal = () => {
  showTournamentResultsModal.value = false;
};

const closeRosterModal = () => {
  showRosterModal.value = false;
};

const handleParticipantsUpdated = async () => {
  try {
    // Reload event data to get updated participant count
    const response = await axios.get(`/api/v1/events/${event.value.id}`);
    const updatedEvent = response.data;

    // Update the event prop by emitting to parent
    emit('event-updated', updatedEvent);
  } catch (error) {
    console.error('Error updating event data:', error);
  }
};

const join = async (event: any) => {
  // For tournament events, show roster selection modal
  if (event.attributes.event_type === 3 || event.attributes.event_type === 'tournament') {
    showRosterModal.value = true;
    return;
  }

  // For non-tournament events, join directly
  try {
    const url = "/api/v1/events/join";
    await axios.post(url, { event_id: event.id });
    event.attributes.joinable = false;
    event.attributes.joined = true;
  } catch (error) {
    console.log(error);
  }
};

const joinWithRoster = async (rosterIds: number[]) => {
  try {
    const url = "/api/v1/events/join";
    await axios.post(url, {
      event_id: event.value.id,
      roster_id: rosterIds
    });
    event.value.attributes.joinable = false;
    event.value.attributes.joined = true;
    showRosterModal.value = false;
  } catch (error: any) {
    console.error('Error joining event:', error);
    // Show error message to user
    if (error.response?.data?.error) {
      alert(error.response.data.error);
    } else {
      alert(t('events.join_error'));
    }
  }
};
</script>

<template>
  <div class="event-card" :class="{ 'tournament-event': isTournament }">
    <!-- Left Section - Avatar and Action Buttons -->
    <div class="event-avatar-section">
      <Avatar
        :src="event.attributes.avatar_url"
        :alt="event.attributes.name"
        size="xlarge"
        :show-frame="true"
        shape="hexagon"
        fallback-icon="bi-calendar-event"
      />

      <div class="avatar-actions">
        <button
          v-if="event.attributes.joinable"
          @click="join(event)"
          class="btn btn-participate"
        >
          {{ t('events.participate') }}
        </button>

        <div v-if="event.attributes.joined" class="joined-status">
          <i class="bi bi-check-circle-fill"></i>
          {{ t('events.registered') }}
        </div>

        <div class="avatar-actions-spacer"></div>

        <button
          v-if="isTournament && (event.attributes.joined || event.attributes.approved_participant_count > 0)"
          @click="openTournamentResultsModal"
          class="btn btn-participate"
        >
          <i class="bi bi-trophy"></i>
          {{ t('events.results') }}
        </button>
      </div>
    </div>

    <!-- Right Section - Event Content -->
    <div class="event-content-section">
      <!-- Top Section - Header -->
      <div class="event-header">
        <div class="event-title-section">
          <h3 v-if="event.attributes.name" class="event-title">
            {{ event.attributes.name }}
          </h3>
        </div>
        <div class="event-header-right">
          <div class="event-location">
            {{ event.attributes.human_city }}
          </div>
          <div class="event-type-badge" :class="{ 'tournament-badge': isTournament }">
            {{ eventTypeName }}
          </div>
        </div>
      </div>

      <!-- Middle Section - Description -->
      <div v-if="event.attributes.description || (isTournament && tournamentRoundsCount > 0)" class="event-description">
        <div class="description-container">
          <div class="description-text" v-if="event.attributes.description">
            <div v-if="!isDescriptionExpanded" v-html="truncatedDescription"></div>
            <span v-if="showReadMore && !isDescriptionExpanded">...</span>
          </div>
          <div v-if="showReadMore && !isDescriptionExpanded" class="read-more-container">
            <a
              @click.prevent="toggleDescription"
              class="read-more-link"
              href="#"
            >
              {{ t('events.read_more') }}
            </a>
          </div>
          <transition
            @before-enter="onBeforeEnter"
            @enter="onEnter"
            @before-leave="onBeforeLeave"
            @leave="onLeave"
          >
            <div
              v-if="isDescriptionExpanded"
              class="description-expanded"
            >
              <div v-if="event.attributes.description" class="description-full-text" v-html="event.attributes.description"></div>

              <!-- Tournament Info Section -->
              <div v-if="isTournament && tournamentRoundsCount > 0" class="tournament-info-section">
                <div v-if="tournamentMissions.length > 0" class="tournament-missions">
                  <div v-if="event.attributes.description || (isTournament && tournamentRoundsCount > 0)" class="event-divider">
                    <img src="@/images/img/divider.png" alt="" />
                  </div>

                  <div class="tournament-info-item">
                    <span class="tournament-info-label">{{ t('events.rounds_and_scenarios') }}</span>
                  </div>

                  <div class="missions-list">
                    <div
                      v-for="mission in tournamentMissions"
                      :key="mission.id"
                      class="mission-item"
                    >
                      <div class="mission-round-number">{{ t('events.round') }} {{ mission.roundNumber }}</div>
                      <div class="mission-scenario-name">{{ mission.scenarioName }}</div>
                      <div v-if="mission.mapName" class="mission-map-name">{{ mission.mapName }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="collapse-link-container">
                <a
                  @click.prevent="toggleDescription"
                  class="read-more-link"
                  href="#"
                >
                  {{ t('events.collapse') }}
                </a>
                <div class="admin-actions-divider">
                  <img src="@/images/img/decor.png" alt="" class="decor-image" />
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Divider between description and details -->
      <div v-if="event.attributes.description || (isTournament && tournamentRoundsCount > 0)" class="event-divider">
        <img src="@/images/img/divider.png" alt="" />
      </div>

      <!-- Event Details Section -->
      <div class="event-details-section">
        <div class="event-details">
          <span class="detail-item">{{ formatDate(event.attributes.starts_at) }}</span>
          <span class="detail-item">{{ event.attributes.approved_participant_count || 0 }} / {{ event.attributes.max_players }} {{ t('events.participants') }}</span>
          <span class="detail-item">{{ event.attributes.price }} RUB</span>
          <span class="detail-item">{{ event.attributes.roster_format }} {{ t('events.points') }}</span>
        </div>
      </div>

      <!-- Admin Actions Row -->
      <div v-if="event.attributes.editable" class="admin-actions-row">
        <!-- Decor that takes all available space -->
        <div class="admin-actions-divider">
          <img src="@/images/img/decor.png" alt="" class="decor-image" />
        </div>
        <!-- Admin management buttons -->
        <div class="admin-actions-buttons">
          <router-link
            :to="{ name: 'edit_event', params: { id: event.id } }"
            class="btn btn-participate"
          >
            <i class="bi bi-pencil"></i>
            {{ t('events.edit') }}
          </router-link>
          <button
            @click="openParticipantModal"
            class="btn btn-participate"
          >
            <i class="bi bi-people"></i>
            {{ t('events.manage_participants') }}
          </button>
          <router-link
            v-if="isTournament"
            :to="{ name: 'tournament_management', params: { id: event.id } }"
            class="btn btn-participate"
          >
            <i class="bi bi-gear"></i>
            {{ t('events.manage_tournament') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <ParticipantManagementModal
    :event-id="Number(event.id)"
    :is-visible="showParticipantModal"
    @close="closeParticipantModal"
    @participants-updated="handleParticipantsUpdated"
  />

  <TournamentResultsModal
    :event-id="Number(event.id)"
    :is-visible="showTournamentResultsModal"
    @close="closeTournamentResultsModal"
  />

  <RosterSelectionModal
    :event-id="Number(event.id)"
    :roster-format-id="event.attributes.roster_format_id"
    :roster-format-points="event.attributes.roster_format"
    :is-visible="showRosterModal"
    :required-rosters-count="requiredRostersCount"
    @close="closeRosterModal"
    @join="joinWithRoster"
  />
</template>

<style lang="scss" scoped>
// Color Variables - Using CSS Variables
$primary-bg: var(--light-base);
$primary-text: var(--primary-text);
$icon-bg: var(--icon-bg);
$icon-text: var(--icon-text);
$hover-bg: var(--hover-bg);
$border-color: var(--border-color);
$focus-shadow: rgba(189, 156, 101, 0.2);
$tournament-bg: var(--tournament-bg);
$tournament-border: var(--tournament-border);
$tournament-text: var(--tournament-text);

.event-card {
  display: flex;
  align-items: stretch;
  background-color: var(--light-base) !important; // Explicit fallback
  background-color: var(--primary-bg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  transition: var(--transition-normal);
  box-shadow: 0 2px 4px var(--shadow-light);
  gap: var(--spacing-xl);
  min-height: 120px; // Ensure minimum height

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px var(--shadow-medium);
  }

  &.tournament-event {
    background-color: var(--tournament-bg);
    border-color: var(--tournament-border);
  }
}

// Left Section - Avatar and Actions
.event-avatar-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);
  min-width: 150px;
  justify-content: flex-start;
}

.avatar-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
  width: 100%;
  margin-top: auto;
}

.avatar-actions-spacer {
  flex: 1;
  min-height: var(--spacing-md);
}

// Right Section - Content
.event-content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  justify-content: space-between;
}

// Top Section - Header
.event-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-md);
}

.event-title-section {
  flex: 1;
}

.event-title {
  /* font-size: var(--font-size-2xl); */
  font-size: 26pt;
  font-weight: bold;
  color: var(--dark-base);
  margin: 0;
  line-height: 1.2;
}

.event-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.event-location {
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: bold;
  text-align: right;
}

.event-type-badge {
  background-color: var(--accent-light);
  color: var(--text-primary);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--border-radius-xl);
  font-size: var(--font-size-xs);
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;

  /*&.tournament-badge {
    background-color: var(--warning-color);
    color: white;
    border: none;
  }*/
}

// Middle Section - Description
.event-description {
  color: var(--text-primary);
  font-size: var(--font-size-xl);
  line-height: 1.5;
  margin: 0;

  .description-container {
    position: relative;
  }

  .description-text {
    display: block;
  }

  .read-more-container {
    display: block;
    margin-top: var(--spacing-xs);
  }

  .description-expanded {
    display: block;
    margin-top: var(--spacing-xs);
    overflow: hidden;
  }

  .description-full-text {
    display: block;
    margin-bottom: var(--spacing-xs);
  }

  .description-text :deep(p),
  .description-full-text :deep(p) {
    margin: 0.5em 0;
  }

  .description-text :deep(h1),
  .description-text :deep(h2),
  .description-text :deep(h3),
  .description-full-text :deep(h1),
  .description-full-text :deep(h2),
  .description-full-text :deep(h3) {
    margin: 0.5em 0;
    font-weight: bold;
  }

  .description-text :deep(ul),
  .description-text :deep(ol),
  .description-full-text :deep(ul),
  .description-full-text :deep(ol) {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }

  .description-text :deep(strong),
  .description-full-text :deep(strong) {
    font-weight: bold;
  }

  .description-text :deep(em),
  .description-full-text :deep(em) {
    font-style: italic;
  }

  .read-more-link {
    color: var(--text-primary);
    text-decoration: none;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;
    display: inline-block;
    color: var(--accent-light);

    &:hover {
      // color: var(--hover-bg);
      text-decoration: underline;
    }
  }

  .collapse-link-container {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    margin-top: var(--spacing-xs);

    .admin-actions-divider {
      flex: 0 0 50%;
      min-width: 0;
      justify-content: center;
      margin: 0 auto;
    }
  }
}

// Slide down/up animation for description
// Animation is handled by JavaScript hooks for smooth height transitions

// Tournament Info Section (in description section)
.tournament-info-section {
  width: 100%;
  padding: var(--spacing-sm);
  margin-top: var(--spacing-md);
  margin-bottom: var(--spacing-sm);

  .tournament-info-item {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-xs);
    font-size: var(--font-size-sm);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .tournament-info-label {
    font-weight: bold;
    color: var(--text-primary);
    font-size: var(--font-size-lg);
    white-space: nowrap;
  }

  .tournament-missions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    margin-top: var(--spacing-xs);

    .tournament-info-label {
      margin-bottom: var(--spacing-xs);
      font-style: bold;
      font-weight: bolder;
      font-size: var(--font-size-lg);
    }

    .missions-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: left;
      gap: var(--spacing-md);
      font-size: var(--font-size-xs);
    }

    .mission-item {
      display: flex;
      flex-direction: column;
      align-items: left;
      text-align: left;
      // color: var(--icon-text);
      font-size: var(--font-size-xs);
      line-height: 1.4;
      word-break: break-word;
      flex: 0 1 200px;
      min-width: 150px;
      padding: var(--spacing-sm);
      // border: 1px solid var(--border-color);
      // border-radius: var(--border-radius-sm);
      // background-color: var(--primary-bg);

      .mission-round-number {
        font-size: var(--font-size-lg);
        font-weight: bold;
        margin-bottom: var(--spacing-xs);
        color: var(--text-primary);
      }

      .mission-scenario-name {
        margin-bottom: var(--spacing-xs);
        font-size: var(--font-size-lg);
        font-style: bold;
        font-weight: bolder;
      }

      .mission-map-name {
        color: var(--text-primary);
        // font-style: italic;
        font-size: var(--font-size-lg);
      }
    }
  }
}

// Divider between description and details
.event-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: var(--spacing-xs) 0;

  img {
    max-width: 100%;
    height: auto;
  }
}

// Event Details Section
.event-details-section {
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-md);
  border-top: 1px solid rgba($border-color, 0.3);
}

.event-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-xl);
  color: var(--text-primary);
  flex-wrap: wrap;
  width: 100%;
  gap: var(--spacing-lg);
}

.detail-item {
  white-space: nowrap;
  font-weight: bold;
}

// Button Styles
.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-lg);
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: bold;
  cursor: pointer;
  transition: var(--transition-fast);
  text-decoration: none;

  &:hover {
    transform: translateY(-1px);
  }

  &.btn-participate {
    background-color: var(--accent-light);
    color: var(--text-primary);
    border: 2px solid var(--border-color);

    &:hover {
      background-color: var(--hover-bg);
      border-color: var(--hover-bg);
      box-shadow: 0 2px 4px var(--shadow-light);
    }
  }

  &.btn-tournament-results {
    background-color: var(--info-color);
    color: white;

    &:hover {
      background-color: var(--info-hover);
    }
  }


}

.joined-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background-color: rgba(255, 255, 255, 0.8);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  color: var(--text-primary);
  font-weight: bold;
  font-size: var(--font-size-sm);
  box-shadow: 0 1px 3px var(--shadow-light);

  i {
    font-size: var(--font-size-base);
    color: var(--icon-bg);
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 2px 4px var(--shadow-light);
  }
}

// Admin Actions Row
.admin-actions-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding-top: var(--spacing-md);
  border-top: 1px solid rgba($border-color, 0.3);
  gap: var(--spacing-md);
}

.admin-actions-divider {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  overflow: hidden;

  .decor-image {
    width: 100%;
    height: auto;
    object-fit: fill;
    object-position: left center;
    display: block;
  }
}

.admin-actions-buttons {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;

  .btn {
    white-space: nowrap;
    min-width: fit-content;
    flex-shrink: 0;

    // Ensure text doesn't wrap vertically
    writing-mode: horizontal-tb;
    text-orientation: mixed;

    // Add minimum width to prevent text compression
    min-width: 120px;

    // Center text properly
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

// Responsive design
@media (max-width: 768px) {
  .event-card {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    flex-direction: column;
    gap: var(--spacing-md);
    padding: var(--spacing-lg);
    width: 100% !important;
    min-height: 120px !important;
    margin-bottom: var(--spacing-xl) !important;
    position: relative !important;
    z-index: 1 !important;
  }

  .event-avatar-section {
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-lg);
  }

  .avatar-actions {
    flex-direction: row;
    align-items: center;
    width: auto;
  }

  .tournament-info-section {
    width: auto;
    min-width: 150px;
    max-width: 200px;
  }

  .event-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-sm);
  }

  .event-details-section {
    margin-top: var(--spacing-sm);
  }

  .event-details {
    justify-content: space-between;
    text-align: center;
    gap: var(--spacing-md);
  }

  .admin-actions-row {
    justify-content: center;
  }

  .admin-actions-buttons {
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-xs);

    .btn {
      width: 100%;
      max-width: 200px;
      min-width: 150px;
    }
  }
}

@media (max-width: 480px) {
  .event-details {
    flex-direction: column;
    gap: var(--spacing-xs);
    text-align: center;
  }

  .detail-item {
    white-space: normal;
  }

  .admin-actions-buttons {
    .btn {
      font-size: var(--font-size-xs);
      padding: var(--spacing-xs) var(--spacing-sm);
      min-width: 120px;
    }
  }
}
</style>
